import { useEffect } from 'react';

/**
 * useSEO — sets per-route <title>, <meta name="description">, and
 * <link rel="canonical"> at mount time. Captured by react-snap at build
 * time (puppeteer executes useEffect before snapshotting), so Googlebot
 * sees unique, per-URL metadata on the FIRST fetch — fixes the
 * "Discovered - currently not indexed" issue caused by every SPA route
 * returning the identical index.html shell.
 *
 * Restores the previous values on unmount so client-side navigation back
 * to another route doesn't leak the previous page's title.
 *
 * @param {string} title             — unique page title
 * @param {string} description       — unique meta description
 * @param {string} canonicalPath     — path (e.g. "/pa") used to build the
 *                                      absolute canonical URL. Omit to
 *                                      skip canonical injection.
 */
export function useSEO(title, description, canonicalPath) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    // description
    let metaEl = document.querySelector('meta[name="description"]');
    const prevDesc = metaEl ? metaEl.getAttribute('content') : null;
    if (description) {
      if (!metaEl) {
        metaEl = document.createElement('meta');
        metaEl.setAttribute('name', 'description');
        document.head.appendChild(metaEl);
      }
      metaEl.setAttribute('content', description);
    }

    // canonical
    let linkEl = document.querySelector('link[rel="canonical"]');
    const prevHref = linkEl ? linkEl.getAttribute('href') : null;
    if (canonicalPath) {
      const origin =
        (typeof window !== 'undefined' && window.location && window.location.origin) ||
        'https://apply.thelemonpros.com';
      const href = `${origin}${canonicalPath.startsWith('/') ? '' : '/'}${canonicalPath}`;
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.setAttribute('rel', 'canonical');
        document.head.appendChild(linkEl);
      }
      linkEl.setAttribute('href', href);
    }

    return () => {
      document.title = prevTitle;
      if (metaEl && prevDesc !== null) metaEl.setAttribute('content', prevDesc);
      if (linkEl && prevHref !== null) linkEl.setAttribute('href', prevHref);
      else if (linkEl && prevHref === null) linkEl.remove();
    };
  }, [title, description, canonicalPath]);
}
